import { ExportOutlined } from '@ant-design/icons'
import { LegalDocumentVersionNew } from '@components/legalDocumentVersion/LegalDocumentVersionNew'
import { LegalDocumentVersionRead } from '@components/legalDocumentVersion/LegalDocumentVersionRead'
import { Localizable } from '@components/localizable/Localizable'
import Page from '@components/Page/Page'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  getLegalDocumentWithSlug,
  selectLegalDocumentBySlug,
} from '@store/LegalDocumentReducer'
import {
  getLegalDocumentVersions,
  selectLegalDocumentVersions,
  setLegalDocumentVersionQuery,
  setSelectedLegalDocumentVersion,
} from '@store/LegalDocumentVersionReducer'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { Permission } from '@utils/permissions'
import {
  Button,
  Card,
  Col,
  Menu,
  Popconfirm,
  Row,
  Space,
  Tag,
  Typography,
} from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

const { Text } = Typography

const LegalDocumentVersionPage = () => {
  const { legalDocumentSlug, version } = useParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const legalDocument = useAppSelector(
    selectLegalDocumentBySlug(legalDocumentSlug ?? '')
  )
  const legalDocumentVersions = useAppSelector(selectLegalDocumentVersions())

  const [blankVersion, setBlankVersion] = useState<boolean>(true)

  const fetchLegalDocumentVersions = () => {
    if (!legalDocumentSlug) return

    const query: TableParams = {
      ...initialQuery,
      columnKey: 'version',
      order: 'ascend',
      filters: { legalDocument: [legalDocumentSlug] },
    }

    dispatch(setLegalDocumentVersionQuery(query))
    dispatch(getLegalDocumentVersions(legalDocumentSlug, query))
  }

  useEffect(() => {
    if (!legalDocumentSlug) {
      navigate('/home')
      return
    }

    if (!legalDocument) {
      dispatch(getLegalDocumentWithSlug(legalDocumentSlug)).catch(() =>
        navigate('/home')
      )
    }

    fetchLegalDocumentVersions()
  }, [legalDocumentSlug])

  useEffect(() => {
    if (!version || legalDocumentVersions.length === 0) return

    const currentVersion = legalDocumentVersions.find(
      (v) => String(v.version) === String(version)
    )

    if (currentVersion?.id) {
      dispatch(setSelectedLegalDocumentVersion(currentVersion.id))
    }
  }, [version, legalDocumentVersions, dispatch])

  const menuItems = legalDocumentVersions.map((item) => ({
    key: String(item.version),
    label: (
      <Link
        to={version ? `../${item.version}` : String(item.version)}
        style={{ width: '100%' }}
      >
        <Space
          content="space-between"
          style={{
            width: '100%',
            justifyContent: 'space-between',
          }}
        >
          <Space>
            <Tag>V{item.version}</Tag>
            {item.isActive && <Tag color="green">Active</Tag>}
          </Space>
          {dayjs(item.updatedAt).format('DD-MM-YYYY')}
        </Space>
      </Link>
    ),
  }))

  const confirm = (mode: 'blank' | 'active') => {
    setBlankVersion(mode === 'blank')

    navigate(`../`)
  }

  if (!legalDocument && legalDocumentSlug) return null

  return (
    <Page
      permissions={Permission.listLegalDocument}
      noWrapWithCol
      pageTitle={{
        title: legalDocument?.friendlyName,
        level: 4,
        toolbar: legalDocument?.frontendUrl && (
          <Button
            type="primary"
            icon={<ExportOutlined />}
            href={legalDocument?.frontendUrl}
            target="_blank"
          >
            View
          </Button>
        ),
      }}
    >
      <Row gutter={[16, 16]} style={{ width: '100%' }}>
        <Col span={6}>
          <Card
            title="Versions"
            extra={
              <Popconfirm
                title="Create a new version"
                description={'Choose how you want to create the version'}
                okText="Blank"
                cancelText="Active version"
                onConfirm={() => confirm('blank')}
                onCancel={() => confirm('active')}
              >
                <Button type="primary" disabled={!version}>
                  Add
                </Button>
              </Popconfirm>
            }
            styles={
              legalDocumentVersions.length > 0 && { body: { padding: 1 } }
            }
          >
            {legalDocumentVersions.length > 0 ? (
              <Menu
                selectedKeys={[version ?? '']}
                items={menuItems}
                style={{ border: 'none' }}
              />
            ) : (
              <Text>No versions</Text>
            )}
          </Card>
        </Col>
        <Col style={{ flex: 1, minWidth: 0 }}>
          <Localizable headerSelector>
            {version ? (
              <LegalDocumentVersionRead
                onRefresh={fetchLegalDocumentVersions}
              />
            ) : (
              <LegalDocumentVersionNew
                onRefresh={fetchLegalDocumentVersions}
                isBlank={blankVersion}
              />
            )}
          </Localizable>
        </Col>
      </Row>
    </Page>
  )
}

export default LegalDocumentVersionPage
